import React, { useEffect } from 'react';
import { Pencil as CreateOutlinedIcon } from 'lucide-react';

import { useAppDispatch, useAppSelector } from 'src/store';
import { getCurrentCustomerId } from 'src/app/customers/state/customersSelector';
import {
    setEditUserFormMode,
    setEditUserFormState,
    updateGeneralUiState,
    updaterUserManagementListFilterState,
} from 'src/app/admin/views/user-management/state/userManagementSlice';
import useUserManagementList from 'src/app/admin/views/user-management/hooks/useUserManagementList';

import { TertiaryButton } from 'src/shared/components/button/Button';
import { Access, Status } from 'src/shared/types';
import { DataTableColumns } from 'src/shared/components/data-table/dataTableTypes';
import { DataTableState, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import DataTable from 'src/shared/components/data-table/DataTable';
import StatusIndicator from 'src/shared/components/status-indicator/StatusIndicator';
import StatusChip from 'src/shared/components/status-chip/StatusChip';

import UserDetails from 'src/app/admin/views/user-management/components/user-details/UserDetails';
import {
    getUserStatusFromEnum,
    getCustomersForEditUser,
    getProjectsForEditUser,
} from 'src/app/admin/views/user-management/userManagementHelpers';
import styles from './CustomerAdminUserManagementList.module.scss';

const tableColumns: DataTableColumns = [
    {
        columnId: 'userId',
        title: 'User Id',
        width: '0',
    },
    {
        columnId: 'customerName',
        title: 'Name',
        width: '400px',
    },
    {
        columnId: 'accountStatus',
        title: 'Account Status',
        width: '200px',
    },
    {
        columnId: 'accessToThisOrganisation',
        title: 'Access to this organisation',
        width: '200px',
    },
    {
        columnId: 'assignedProjects',
        title: 'Assigned Sites',
        width: '90%',
    },
    {
        columnId: 'actions',
        title: 'Actions',
    },
];

const CustomerAdminUserManagementList = () => {
    const currentCustomerId = useAppSelector(getCurrentCustomerId) || '';
    const dispatch = useAppDispatch();

    const { isFetchingCustomerUsers, currentDataForCustomerAdmin, sortedUsersForCustomerAdmin, tableState, setTableState } =
        useUserManagementList();

    const onRowSelect = (rowData: TableRowData) => {
        const userId = rowData[0] as string;
        if (userId) {
            const editUserDetails = sortedUsersForCustomerAdmin.filter(Boolean).find(user => user.cognitoId === userId);
            if (!editUserDetails) {
                return;
            }
            // For View formstate - Get Roles, Project list without name and customers without projects
            const userRoles = editUserDetails.customers.find(customer => customer.customerId === currentCustomerId)?.roles;
            const projectsForEditUser = getProjectsForEditUser(editUserDetails.customers);
            const customersForEditUser = getCustomersForEditUser(editUserDetails.customers);
            dispatch(
                setEditUserFormState({
                    ...editUserDetails,
                    projects: projectsForEditUser || [],
                    roles: userRoles ?? [],
                    customers: customersForEditUser || [],
                })
            );

            dispatch(updateGeneralUiState({ shouldShowViewUserModal: true }));
        }
    };

    useEffect(() => {
        if (!currentDataForCustomerAdmin) {
            return;
        }

        const tableData: DataTableState['data'] = [];

        sortedUsersForCustomerAdmin.forEach(user => {
            const basicDetail = <UserDetails firstName={user.firstName} lastName={user.lastName} email={user.email} status={user.status} />;

            const statusColumn = (
                <StatusChip label={user.status === 'Inactive' ? 'Deactivated' : user.status} status={getUserStatusFromEnum(user.status)} />
            );

            // Display first 3 projects as tag and rest as +X
            const currentCustomerUserProjects = user.customers.find(customer => customer.customerId === currentCustomerId)?.projects || [];
            const assignedProjects = (
                <div className={styles.projectTags}>
                    {currentCustomerUserProjects.slice(0, 3).map((project, index) => {
                        return (
                            <span key={index} className={`${styles.projectTag} ${styles['projectTag' + user.status]}`}>
                                {project.projectName}
                            </span>
                        );
                    })}
                    {currentCustomerUserProjects.length > 3 && (
                        <span className='bg-grey-20 px-2 py-1 rounded-xl'>+{currentCustomerUserProjects.length - 3}</span>
                    )}
                </div>
            );

            const editActions = (
                <TertiaryButton
                    className={styles.actions}
                    data-testid={`user-management-row-edit-${user.cognitoId}`}
                    onClick={e => {
                        e.stopPropagation();
                        // For Edit formstate - Get Project list without name and customers without projects
                        const projectsForEditUser = getProjectsForEditUser(user.customers);
                        const customersForEditUser = getCustomersForEditUser(user.customers);
                        const userRolesForCustomer = user.customers.find(c => c.customerId === currentCustomerId)?.roles;
                        dispatch(updateGeneralUiState({ shouldShowEditUserModal: true }));
                        dispatch(
                            setEditUserFormState({
                                ...user,
                                projects: projectsForEditUser || [],
                                customers: customersForEditUser || [],
                                roles: userRolesForCustomer ?? [],
                            })
                        );
                        dispatch(setEditUserFormMode(Access.UPDATE));
                    }}
                >
                    <CreateOutlinedIcon /> <span>EDIT</span>
                </TertiaryButton>
            );

            const isAssignedToCustomer = user.customers.some(customer => customer.customerId === currentCustomerId && customer.active);
            const isAssignedToCustomerColumn = (
                <StatusIndicator
                    status={isAssignedToCustomer ? Status.SUCCESS : Status.ERROR}
                    statusText={isAssignedToCustomer ? 'Assigned' : 'Unassigned'}
                />
            );

            const tableRowData = [user.cognitoId, basicDetail, statusColumn, isAssignedToCustomerColumn, assignedProjects, editActions];

            tableData.push(tableRowData);
        });

        setTableState(prev => ({ ...prev, data: tableData, currentPageNumber: currentDataForCustomerAdmin?.pagination.page || 1 }));
    }, [currentDataForCustomerAdmin]);

    const emptyStateMessage = (
        <div className={styles.noRecordsMessage}>
            There were no user accounts found matching this selection. Adjust the filters to find users in your organisation.
        </div>
    );

    const onPageChange = (page: number) => {
        dispatch(updaterUserManagementListFilterState({ currentPageNumber: page }));
    };

    const isTableLoading = tableState.currentPageNumber !== currentDataForCustomerAdmin?.pagination.page;
    const showTableLoadingOverlay = isFetchingCustomerUsers || isTableLoading;

    return (
        <div className={styles.userManagementList} data-testid='user-management-user-table'>
            <DataTable
                className={styles.dataTableStretch}
                state={{ ...tableState, data: tableState.data ?? [] }}
                columns={tableColumns}
                onStateChange={setTableState}
                emptyStateMessage={emptyStateMessage}
                onRowSelect={onRowSelect}
                onPageChange={onPageChange}
                totalRowsForPagination={currentDataForCustomerAdmin?.pagination.total}
                isLoadingOverlay={showTableLoadingOverlay}
            />
        </div>
    );
};

export default CustomerAdminUserManagementList;
